/**
 * Константа скорости анимации центрирования вида стейджа (в миллисекундах)
 * Чем меньше значение, тем быстрее анимация
 */
export const CENTER_ANIMATION_DURATION = 650; // мс
/**
 * Easing функция для плавной анимации (ease-in-out-cubic)
 * Обеспечивает плавное ускорение в начале и замедление в конце
 *
 * @param t - Прогресс анимации от 0 до 1
 * @returns Значение easing от 0 до 1
 */
function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
/**
 * Проверяет, находится ли элемент в видимой области viewport
 *
 * @param element - Элемент доски
 * @param stagePosition - Позиция Stage (x, y)
 * @param zoom - Масштаб
 * @param viewportWidth - Ширина viewport
 * @param viewportHeight - Высота viewport
 * @param buffer - Буферная зона в пикселях экрана для расширения области (по умолчанию 200px)
 * @returns true, если элемент видим
 */
export function isElementInViewport(element, stagePosition, zoom, viewportWidth, viewportHeight, buffer = 200) {
    var _a, _b, _c, _d;
    // Всегда используем полные размеры элемента для определения bounding box
    // Маска (clip) определяет видимую часть ВНУТРИ элемента, но сам элемент должен
    // рендериться, если хотя бы часть его bounding box видна
    const elX = ((_a = element.props) === null || _a === void 0 ? void 0 : _a.x) || 0;
    const elY = ((_b = element.props) === null || _b === void 0 ? void 0 : _b.y) || 0;
    const elW = ((_c = element.props) === null || _c === void 0 ? void 0 : _c.w) || 64;
    const elH = ((_d = element.props) === null || _d === void 0 ? void 0 : _d.h) || 64;
    // Вычисляем область видимости в мировых координатах
    // stagePosition - это смещение Stage в экранных координатах
    // zoom - масштаб (scaleX/scaleY)
    const viewportLeft = -stagePosition.x / zoom;
    const viewportTop = -stagePosition.y / zoom;
    const viewportRight = viewportLeft + viewportWidth / zoom;
    const viewportBottom = viewportTop + viewportHeight / zoom;
    // Расширяем область видимости для буфера
    // Буфер в экранных пикселях преобразуем в мировые координаты
    const bufferInWorld = buffer / zoom;
    const searchLeft = viewportLeft - bufferInWorld;
    const searchTop = viewportTop - bufferInWorld;
    const searchRight = viewportRight + bufferInWorld;
    const searchBottom = viewportBottom + bufferInWorld;
    // Проверяем пересечение элемента с расширенной областью видимости
    // Используем полный bounding box элемента (x, y, w, h), чтобы не пропустить частично видимые элементы
    const elRight = elX + elW;
    const elBottom = elY + elH;
    // Элемент видим, если его bounding box пересекается с областью видимости
    // Проверяем: правый край элемента >= левый край viewport И левый край элемента <= правый край viewport
    const intersectsHorizontally = elRight >= searchLeft && elX <= searchRight;
    // Проверяем: нижний край элемента >= верхний край viewport И верхний край элемента <= нижний край viewport
    const intersectsVertically = elBottom >= searchTop && elY <= searchBottom;
    return intersectsHorizontally && intersectsVertically;
}
/**
 * Проверяет, находится ли элемент в видимой области viewport для кэширования.
 * Использует расширенную буферную зону (2/3 ширины экрана) для предкэширования элементов.
 *
 * @param element - Элемент доски
 * @param stagePosition - Позиция Stage (x, y)
 * @param zoom - Масштаб
 * @param viewportWidth - Ширина viewport
 * @param viewportHeight - Высота viewport
 * @returns true, если элемент должен быть закэширован
 */
export function isElementInViewportForCache(element, stagePosition, zoom, viewportWidth, viewportHeight) {
    // Используем расширенный буфер 2/3 ширины экрана для предкэширования
    const cacheBuffer = (viewportWidth * 2) / 3;
    return isElementInViewport(element, stagePosition, zoom, viewportWidth, viewportHeight, cacheBuffer);
}
/**
 * Получает область видимости viewport в мировых координатах
 *
 * @param stagePosition - Позиция Stage (x, y)
 * @param zoom - Масштаб
 * @param viewportWidth - Ширина viewport
 * @param viewportHeight - Высота viewport
 * @returns Область видимости в мировых координатах
 */
export function getViewportBounds(stagePosition, zoom, viewportWidth, viewportHeight) {
    const viewportLeft = -stagePosition.x / zoom;
    const viewportTop = -stagePosition.y / zoom;
    const viewportRight = viewportLeft + viewportWidth / zoom;
    const viewportBottom = viewportTop + viewportHeight / zoom;
    return {
        left: viewportLeft,
        top: viewportTop,
        right: viewportRight,
        bottom: viewportBottom,
    };
}
/**
 * Вычисляет позицию стейджа и зум для центрирования элемента в viewport
 *
 * @param element - Элемент доски для центрирования
 * @param viewportWidth - Ширина viewport
 * @param viewportHeight - Высота viewport
 * @param padding - Отступ от краёв viewport в пикселях (по умолчанию 50px)
 * @param minZoom - Минимальный зум (по умолчанию 0.1)
 * @param maxZoom - Максимальный зум (по умолчанию 10)
 * @returns Объект с новой позицией стейджа и зумом, или null если элемент невалиден
 */
export function calculateStageViewToCenterElement(element, viewportWidth, viewportHeight, padding = 50, minZoom = 0.1, maxZoom = 10) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const elX = (_b = (_a = element.props) === null || _a === void 0 ? void 0 : _a.x) !== null && _b !== void 0 ? _b : 0;
    const elY = (_d = (_c = element.props) === null || _c === void 0 ? void 0 : _c.y) !== null && _d !== void 0 ? _d : 0;
    const elW = (_f = (_e = element.props) === null || _e === void 0 ? void 0 : _e.w) !== null && _f !== void 0 ? _f : 64;
    const elH = (_h = (_g = element.props) === null || _g === void 0 ? void 0 : _g.h) !== null && _h !== void 0 ? _h : 64;
    // Проверка на валидность элемента
    if (elW <= 0 || elH <= 0) {
        return null;
    }
    // Вычисляем центр элемента в мировых координатах
    const elementCenterX = elX + elW / 2;
    const elementCenterY = elY + elH / 2;
    // Вычисляем доступную область для элемента с учетом отступов
    const availableWidth = viewportWidth - padding * 2;
    const availableHeight = viewportHeight - padding * 2;
    // Вычисляем оптимальный зум для того, чтобы элемент поместился в viewport
    const zoomByWidth = availableWidth / elW;
    const zoomByHeight = availableHeight / elH;
    // Используем меньший зум, чтобы элемент полностью помещался
    const optimalZoom = Math.min(zoomByWidth, zoomByHeight);
    // Ограничиваем зум в допустимых пределах
    const zoom = Math.max(minZoom, Math.min(maxZoom, optimalZoom));
    // Вычисляем позицию стейджа для центрирования элемента
    // Центр viewport должен совпадать с центром элемента после преобразования
    const viewportCenterX = viewportWidth / 2;
    const viewportCenterY = viewportHeight / 2;
    // Для центрирования элемента:
    // viewportCenterX = elementCenterX * zoom + stagePosition.x
    // Отсюда: stagePosition.x = viewportCenterX - elementCenterX * zoom
    const stagePosition = {
        x: viewportCenterX - elementCenterX * zoom,
        y: viewportCenterY - elementCenterY * zoom,
    };
    return { stagePosition, zoom };
}
/**
 * Вычисляет позицию стейджа для центрирования элемента без изменения зума
 *
 * @param element - Элемент доски для центрирования
 * @param currentZoom - Текущий зум стейджа
 * @param viewportWidth - Ширина viewport
 * @param viewportHeight - Высота viewport
 * @returns Новую позицию стейджа или null если элемент невалиден
 */
export function calculateStagePositionToCenterElement(element, currentZoom, viewportWidth, viewportHeight) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const elX = (_b = (_a = element.props) === null || _a === void 0 ? void 0 : _a.x) !== null && _b !== void 0 ? _b : 0;
    const elY = (_d = (_c = element.props) === null || _c === void 0 ? void 0 : _c.y) !== null && _d !== void 0 ? _d : 0;
    const elW = (_f = (_e = element.props) === null || _e === void 0 ? void 0 : _e.w) !== null && _f !== void 0 ? _f : 64;
    const elH = (_h = (_g = element.props) === null || _g === void 0 ? void 0 : _g.h) !== null && _h !== void 0 ? _h : 64;
    // Проверка на валидность элемента
    if (elW <= 0 || elH <= 0) {
        return null;
    }
    // Вычисляем центр элемента в мировых координатах
    const elementCenterX = elX + elW / 2;
    const elementCenterY = elY + elH / 2;
    // Вычисляем позицию стейджа для центрирования элемента
    const viewportCenterX = viewportWidth / 2;
    const viewportCenterY = viewportHeight / 2;
    return {
        x: viewportCenterX - elementCenterX * currentZoom,
        y: viewportCenterY - elementCenterY * currentZoom,
    };
}
/**
 * Плавно анимирует переход вида стейджа от текущей позиции/зума к целевой
 *
 * @param params - Параметры анимации
 * @returns Функция для отмены анимации
 */
export function animateStageViewToCenter({ currentPosition, currentZoom, targetPosition, targetZoom, onPositionUpdate, onZoomUpdate, duration = CENTER_ANIMATION_DURATION, }) {
    const startTime = performance.now();
    let animationFrameId = null;
    let cancelled = false;
    const startX = currentPosition.x;
    const startY = currentPosition.y;
    const startZoom = currentZoom;
    const deltaX = targetPosition.x - startX;
    const deltaY = targetPosition.y - startY;
    const deltaZoom = targetZoom - startZoom;
    const animate = (currentTime) => {
        if (cancelled)
            return;
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // Применяем easing функцию для плавной анимации
        const easedProgress = easeInOutCubic(progress);
        // Вычисляем промежуточные значения
        const currentX = startX + deltaX * easedProgress;
        const currentY = startY + deltaY * easedProgress;
        const currentZoomValue = startZoom + deltaZoom * easedProgress;
        // Обновляем позицию и зум
        onPositionUpdate({ x: currentX, y: currentY });
        onZoomUpdate(currentZoomValue);
        // Продолжаем анимацию, если не достигли конечной точки
        if (progress < 1) {
            animationFrameId = requestAnimationFrame(animate);
        }
    };
    // Запускаем анимацию
    animationFrameId = requestAnimationFrame(animate);
    // Возвращаем функцию для отмены анимации
    return () => {
        cancelled = true;
        if (animationFrameId !== null) {
            cancelAnimationFrame(animationFrameId);
        }
    };
}
